import { useState, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { TenantProvider, CartProvider } from '@webmono/storefront-ui/providers'
import type { TenantConfig } from '@webmono/storefront-ui/types'
import { mariasTaqueria } from './tenants/marias-taqueria/config'
import { napoliPizza } from './tenants/napoli-pizza/config'
import { sakuraSushi } from './tenants/sakura-sushi/config'
import { TenantSwitcher } from './components/TenantSwitcher'
import { StorefrontLayout } from './routes/StorefrontLayout'
import { MenuPage } from './routes/MenuPage'
import { ItemDetailPage } from './routes/ItemDetailPage'
import { CartPage } from './routes/CartPage'
import { CheckoutPage } from './routes/CheckoutPage'
import { OrderConfirmationPage } from './routes/OrderConfirmationPage'
import { OrderTrackingPage } from './routes/OrderTrackingPage'
import { WalletPage } from './routes/WalletPage'
import { QRCodePage } from './routes/QRCodePage'
import { MerchantLayout } from './routes/merchant/MerchantLayout'
import { MerchantOrdersPage } from './routes/merchant/MerchantOrdersPage'
import { MerchantMenuPage } from './routes/merchant/MerchantMenuPage'
import { MerchantAnalyticsPage } from './routes/merchant/MerchantAnalyticsPage'
import { MerchantCustomersPage } from './routes/merchant/MerchantCustomersPage'

const TENANTS: Record<string, TenantConfig> = {
  marias: mariasTaqueria,
  napoli: napoliPizza,
  sakura: sakuraSushi,
}

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground">Loading...</div>
    </div>
  )
}

export function App() {
  const [currentTenantId, setCurrentTenantId] = useState<string>('marias')
  const tenant = TENANTS[currentTenantId]

  if (!tenant) {
    return <div>Tenant not found</div>
  }

  return (
    <TenantProvider tenant={tenant}>
      <CartProvider>
        <TenantSwitcher
          tenants={TENANTS}
          currentTenantId={currentTenantId}
          onTenantChange={setCurrentTenantId}
        />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Navigate to="/menu" replace />} />

            <Route element={<StorefrontLayout />}>
              <Route path="/menu" element={<MenuPage />} />
              <Route path="/menu/:itemId" element={<ItemDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order/:orderId" element={<OrderConfirmationPage />} />
              <Route path="/order/:orderId/track" element={<OrderTrackingPage />} />
              <Route path="/wallet" element={<WalletPage />} />
            </Route>

            <Route path="/qr/:code" element={<QRCodePage />} />

            <Route path="/merchant" element={<MerchantLayout />}>
              <Route index element={<Navigate to="/merchant/orders" replace />} />
              <Route path="orders" element={<MerchantOrdersPage />} />
              <Route path="menu" element={<MerchantMenuPage />} />
              <Route path="analytics" element={<MerchantAnalyticsPage />} />
              <Route path="customers" element={<MerchantCustomersPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/menu" replace />} />
          </Routes>
        </Suspense>
      </CartProvider>
    </TenantProvider>
  )
}
